import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import {
    Calculator,
    Euro,
    TrendingUp,
    Users,
    Plus,
    Calendar,
    Loader2,
    Info,
    Download,
    Trash2,
    Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const Commissions = () => {
    const { api, user, isSuperAdmin, isAdmin } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [brackets, setBrackets] = useState([]);
    const [summaries, setSummaries] = useState([]);
    const [entries, setEntries] = useState([]);
    const [commercials, setCommercials] = useState([]);
    const [companies, setCompanies] = useState([]);
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedUser, setSelectedUser] = useState('all');
    
    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSimulator, setShowSimulator] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        user_id: '',
        year: currentYear,
        month: currentMonth,
        amount_ht: '',
        company_id: '',
        description: '',
    });
    
    // Simulator state
    const [simulatorAmount, setSimulatorAmount] = useState('');
    const [simulatorResult, setSimulatorResult] = useState(null);

    useEffect(() => {
        fetchData();
    }, [selectedYear, selectedMonth, selectedUser]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('year', selectedYear);
            if (selectedMonth) {
                params.append('month', selectedMonth);
            }
            if (selectedUser && selectedUser !== 'all') {
                params.append('user_id', selectedUser);
            }
            
            const [bracketsRes, summaryRes, entriesRes, usersRes, companiesRes] = await Promise.all([
                api.get('/commissions/brackets'),
                api.get(`/commissions/summary?${params.toString()}`),
                api.get(`/commissions/entries?${params.toString()}`),
                isAdmin() ? api.get('/users') : Promise.resolve({ data: [] }),
                api.get('/companies'),
            ]);
            
            setBrackets(bracketsRes.data.brackets || []);
            setSummaries(summaryRes.data || []);
            setEntries(entriesRes.data || []);
            setCommercials(usersRes.data.filter(u => u.role === 'commercial' && u.is_active) || []);
            setCompanies(companiesRes.data || []);
        } catch (error) {
            console.error('Failed to fetch commission data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleAddEntry = async (e) => {
        e.preventDefault();
        
        // Validation: client obligatoire
        if (!formData.company_id) {
            toast.error('Veuillez sélectionner un client');
            return;
        }
        
        setSubmitting(true);
        
        try {
            await api.post('/commissions/entries', {
                ...formData,
                amount_ht: parseFloat(formData.amount_ht),
                company_id: formData.company_id,
            });
            toast.success('Encaissement enregistré');
            setShowAddModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Failed to add entry:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Supprimer cet encaissement ?')) return;
        
        try {
            await api.delete(`/commissions/entries/${entryId}`);
            toast.success('Encaissement supprimé');
            fetchData();
        } catch (error) {
            console.error('Failed to delete entry:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleSimulate = async () => {
        if (!simulatorAmount || parseFloat(simulatorAmount) <= 0) {
            toast.error('Veuillez entrer un montant valide');
            return;
        }
        
        try {
            const response = await api.post(`/commissions/calculate?ca_ht=${parseFloat(simulatorAmount)}`);
            setSimulatorResult(response.data);
        } catch (error) {
            console.error('Simulation failed:', error);
            toast.error('Erreur lors de la simulation');
        }
    };

    const handleExport = async () => {
        try {
            const params = new URLSearchParams();
            params.append('year', selectedYear);
            if (selectedMonth) {
                params.append('month', selectedMonth);
            }
            
            const response = await api.get(`/commissions/export?${params.toString()}`, { responseType: 'blob' });
            
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `commissions_${selectedYear}${selectedMonth ? `_${selectedMonth.toString().padStart(2, '0')}` : ''}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            toast.success('Export téléchargé');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Erreur lors de l\'export');
        }
    };

    const resetForm = () => {
        setFormData({
            user_id: '',
            year: currentYear,
            month: currentMonth,
            amount_ht: '',
            company_id: '',
            description: '',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    // Calculate totals
    const totalCA = summaries.reduce((sum, s) => sum + (s.total_ca_ht || 0), 0);
    const totalCommission = summaries.reduce((sum, s) => sum + (s.commission_amount || s.total_commission || 0), 0);

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="commissions-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading">Commissions</h1>
                    <p className="text-muted-foreground mt-1">
                        Calcul des commissions variables par tranches progressives
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShowSimulator(true)}
                        className="rounded-full"
                        data-testid="simulator-btn"
                    >
                        <Calculator className="w-4 h-4 mr-2" />
                        Simulateur
                    </Button>
                    {isAdmin() && (
                        <Button
                            onClick={() => {
                                resetForm();
                                setShowAddModal(true);
                            }}
                            className="rounded-full"
                            data-testid="add-entry-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Enregistrer CA
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <Label className="text-xs text-muted-foreground mb-1 block">Année</Label>
                            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                                <SelectTrigger className="rounded-lg" data-testid="year-filter">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <Label className="text-xs text-muted-foreground mb-1 block">Mois</Label>
                            <Select value={selectedMonth?.toString() || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? null : parseInt(v))}>
                                <SelectTrigger className="rounded-lg" data-testid="month-filter">
                                    <SelectValue placeholder="Tous les mois" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les mois</SelectItem>
                                    {MONTHS.map((m, i) => (
                                        <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {isAdmin() && (
                            <div className="flex-1">
                                <Label className="text-xs text-muted-foreground mb-1 block">Commercial</Label>
                                <Select value={selectedUser} onValueChange={setSelectedUser}>
                                    <SelectTrigger className="rounded-lg" data-testid="user-filter">
                                        <Users className="w-4 h-4 mr-2" />
                                        <SelectValue placeholder="Tous" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les commerciaux</SelectItem>
                                        {commercials.map((u) => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {isAdmin() && (
                            <div className="flex items-end">
                                <Button variant="outline" onClick={handleExport} className="rounded-full">
                                    <Download className="w-4 h-4 mr-2" />
                                    Export
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">CA HT Encaissé</p>
                                <p className="text-2xl font-bold">{formatCurrency(totalCA)}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-blue-500/10">
                                <Euro className="w-5 h-5 text-blue-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Commissions</p>
                                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalCommission)}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-green-500/10">
                                <TrendingUp className="w-5 h-5 text-green-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Taux effectif moyen</p>
                                <p className="text-2xl font-bold">
                                    {totalCA > 0 ? ((totalCommission / totalCA) * 100).toFixed(1) : 0}%
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-purple-500/10">
                                <Calculator className="w-5 h-5 text-purple-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="summary" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="summary" data-testid="tab-summary">Récapitulatif</TabsTrigger>
                    <TabsTrigger value="entries" data-testid="tab-entries">Détail des encaissements</TabsTrigger>
                    <TabsTrigger value="brackets" data-testid="tab-brackets">Barème</TabsTrigger>
                </TabsList>

                {/* Summary Tab */}
                <TabsContent value="summary">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">
                                Commissions {selectedMonth ? MONTHS[selectedMonth - 1] : ''} {selectedYear}
                            </CardTitle>
                            <CardDescription>
                                Récapitulatif par commercial
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {summaries.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucune donnée pour cette période</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Commercial</TableHead>
                                            <TableHead className="text-right">CA HT Encaissé</TableHead>
                                            <TableHead className="text-right">Commission</TableHead>
                                            <TableHead className="text-right">Taux effectif</TableHead>
                                            <TableHead className="text-right">Entrées</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {summaries.map((summary) => (
                                            <TableRow key={summary.user_id} data-testid={`summary-row-${summary.user_id}`}>
                                                <TableCell className="font-medium">{summary.user_name}</TableCell>
                                                <TableCell className="text-right">{formatCurrency(summary.total_ca_ht)}</TableCell>
                                                <TableCell className="text-right font-semibold text-green-600">
                                                    {formatCurrency(summary.commission_amount || summary.total_commission || 0)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {summary.rate_effective || (summary.total_ca_ht > 0 ? (((summary.commission_amount || summary.total_commission || 0) / summary.total_ca_ht) * 100).toFixed(1) : 0)}%
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Badge variant="outline">{summary.entries_count}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Entries Tab */}
                <TabsContent value="entries">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Encaissements enregistrés</CardTitle>
                            <CardDescription>
                                Liste détaillée des CA encaissés
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {entries.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Euro className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucun encaissement enregistré</p>
                                    {isAdmin() && (
                                        <Button
                                            variant="link"
                                            onClick={() => {
                                                resetForm();
                                                setShowAddModal(true);
                                            }}
                                            className="mt-2"
                                        >
                                            Enregistrer un encaissement
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Commercial</TableHead>
                                            <TableHead>Période</TableHead>
                                            <TableHead>Client</TableHead>
                                            <TableHead>Description</TableHead>
                                            <TableHead className="text-right">Montant HT</TableHead>
                                            {isSuperAdmin() && <TableHead className="w-12"></TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {entries.map((entry) => (
                                            <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                                                <TableCell className="font-medium">{entry.user_name}</TableCell>
                                                <TableCell>{MONTHS[entry.month - 1]} {entry.year}</TableCell>
                                                <TableCell>{entry.company_name || '-'}</TableCell>
                                                <TableCell className="max-w-xs truncate">{entry.description || '-'}</TableCell>
                                                <TableCell className="text-right font-semibold">{formatCurrency(entry.amount_ht)}</TableCell>
                                                {isSuperAdmin() && (
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeleteEntry(entry.id)}
                                                            className="h-8 w-8 text-destructive hover:text-destructive"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Brackets Tab */}
                <TabsContent value="brackets">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">Barème des commissions</CardTitle>
                            <CardDescription>
                                Barème progressif par tranches cumulatives sur CA HT mensuel encaissé
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                                    <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-medium text-blue-600 dark:text-blue-400">Calcul cumulatif par tranches</p>
                                        <p className="text-muted-foreground mt-1">
                                            Chaque tranche de chiffre d'affaires est rémunérée au taux correspondant, 
                                            de manière cumulative, et non par application d'un taux unique à l'ensemble.
                                        </p>
                                    </div>
                                </div>
                                
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Tranche CA HT mensuel</TableHead>
                                            <TableHead className="text-right">Taux</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {brackets.map((bracket, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {bracket.max === null 
                                                        ? `Au-delà de ${formatCurrency(bracket.min)}`
                                                        : bracket.min === 0
                                                            ? `Jusqu'à ${formatCurrency(bracket.max)}`
                                                            : `De ${formatCurrency(bracket.min)} à ${formatCurrency(bracket.max)}`
                                                    }
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Badge variant={bracket.rate === 0 ? 'secondary' : 'default'} className={bracket.rate > 0.15 ? 'bg-green-500' : ''}>
                                                        {(bracket.rate * 100).toFixed(0)}%
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <Separator />

                                <div className="text-sm text-muted-foreground">
                                    <p>La rémunération variable versée au titre des commissions constitue une rémunération brute, 
                                    soumise aux cotisations sociales et fiscales applicables.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Add Entry Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Enregistrer un encaissement</DialogTitle>
                        <DialogDescription>
                            Ajoutez un montant CA HT encaissé pour un commercial
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleAddEntry} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Commercial *</Label>
                            <Select
                                value={formData.user_id}
                                onValueChange={(v) => setFormData({ ...formData, user_id: v })}
                                required
                            >
                                <SelectTrigger className="rounded-lg" data-testid="entry-user-select">
                                    <SelectValue placeholder="Sélectionner un commercial" />
                                </SelectTrigger>
                                <SelectContent>
                                    {commercials.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Année *</Label>
                                <Select
                                    value={formData.year.toString()}
                                    onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                                >
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Mois *</Label>
                                <Select
                                    value={formData.month.toString()}
                                    onValueChange={(v) => setFormData({ ...formData, month: parseInt(v) })}
                                >
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MONTHS.map((m, i) => (
                                            <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Montant HT encaissé *</Label>
                            <div className="relative">
                                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.amount_ht}
                                    onChange={(e) => setFormData({ ...formData, amount_ht: e.target.value })}
                                    placeholder="0.00"
                                    required
                                    className="pl-9 rounded-lg"
                                    data-testid="entry-amount-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Client *</Label>
                            <SearchableCombobox
                                options={companies.map(c => ({ id: c.id, label: c.legal_name, sublabel: c.city || c.sector }))}
                                value={formData.company_id}
                                onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                                placeholder="Rechercher un client..."
                                emptyMessage="Aucun client trouvé"
                                type="company"
                                data-testid="commission-company-search"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Description (optionnel)</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Ex: Paiement facture #123"
                                className="rounded-lg"
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAddModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="entry-submit-btn">
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Simulator Modal */}
            <Dialog open={showSimulator} onOpenChange={setShowSimulator}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Simulateur de commission</DialogTitle>
                        <DialogDescription>
                            Calculez la commission pour un montant CA HT donné
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Montant CA HT mensuel</Label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={simulatorAmount}
                                        onChange={(e) => {
                                            setSimulatorAmount(e.target.value);
                                            setSimulatorResult(null);
                                        }}
                                        placeholder="0.00"
                                        className="pl-9 rounded-lg"
                                        data-testid="simulator-amount-input"
                                    />
                                </div>
                                <Button onClick={handleSimulate} className="rounded-full" data-testid="simulate-btn">
                                    Calculer
                                </Button>
                            </div>
                        </div>

                        {simulatorResult && (
                            <div className="space-y-4 pt-4 border-t">
                                <div className="flex items-center justify-between p-4 rounded-lg bg-green-500/10">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Commission calculée</p>
                                        <p className="text-3xl font-bold text-green-600">{formatCurrency(simulatorResult.commission)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-muted-foreground">Taux effectif</p>
                                        <p className="text-xl font-semibold">{simulatorResult.rate_effective}%</p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <p className="text-sm font-medium">Détail par tranche :</p>
                                    <div className="space-y-1">
                                        {simulatorResult.breakdown.map((b, i) => (
                                            <div key={i} className="flex justify-between text-sm p-2 rounded bg-muted/50">
                                                <span>{b.bracket}</span>
                                                <span className="text-muted-foreground">{formatCurrency(b.amount)} × {b.rate}</span>
                                                <span className="font-medium">{formatCurrency(b.commission)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowSimulator(false);
                                setSimulatorAmount('');
                                setSimulatorResult(null);
                            }}
                            className="rounded-full"
                        >
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Commissions;
